import React, {useState, useLayoutEffect, useEffect, useRef, useMemo} from 'react';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {NucleotideType} from 'biojs/BioConstants';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import FileStates from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {DNA_SEQUENCE_IMPORT_FLOW} from 'molbio/MolBioConstants';
import {trackNucleotideCreateModalSubmit} from 'molbio/SequenceAnalyticsHelper';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import useOnMount from 'ui-library/hooks/useOnMount';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import BatchChromosomeImportForm from './BatchChromosomeImportForm';
import CreateSequenceForm from './CreateSequenceForm';
import DBImportForm from './DBImportForm';
import ImportFromFileView from './ImportFromFileView';
import {createSequence, importFromDB, importSequencesByChromosomalCoords} from './ImportSequenceActions';
import {
  DBImportSequence,
  EnsemblDBImportAccession,
  SequenceImportOptions,
  ChromosomalImportData,
  RawImportData,
  isBlockedAddgeneResult,
} from './ImportSequenceConstants';

interface Props {
  initialFiles?: Array<File>;
  onRequestClose: () => void;
  openImportResults?: boolean;
  targetFolderId?: string;
  initialTab?: DNA_SEQUENCE_IMPORT_FLOW;
}

function ImportSequenceModal({
  initialFiles = [],
  openImportResults = true,
  targetFolderId,
  onRequestClose,
  initialTab = DNA_SEQUENCE_IMPORT_FLOW.FILE,
}: Props): JSX.Element {
  const [activeTab, setActiveTab] = useState<DNA_SEQUENCE_IMPORT_FLOW>(initialTab);
  const initialTargetFolderId = useMemo(
    () => targetFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY) ?? undefined,
    [targetFolderId]
  );
  const importView = useRef<ImportFromFileView | null>(null);
  const [dbImportSequences, setDBImportSequences] = useState<Array<DBImportSequence>>([]);
  const defaultSequenceImportOptions = {
    folderId: initialTargetFolderId,
    nucleotideType: NucleotideType.DNA,
    tagSchemaId: undefined,
    topology: SequenceTopology.CIRCULAR,
  };
  const [sequenceImportOptions, setSequenceImportOptions] = useState<SequenceImportOptions>(
    defaultSequenceImportOptions
  );
  const [chromosomalData, setChromosomalData] = useState<ChromosomalImportData>({chromosomeCoords: []});
  const [rawImportData, setRawImportData] = useState<RawImportData>({name: 'Untitled sequence', bases: ''});

  useOnMount(
    (): (() => void) =>
      function cleanup() {
        if (importView.current != null) {
          importView.current.remove();
        }
      }
  );

  useEffect(() => {
    if (initialTab === DNA_SEQUENCE_IMPORT_FLOW.FILE) {
      if (importView.current == null) {
        importView.current = new ImportFromFileView({
          closeModal: onRequestClose,
          targetFolderId: initialTargetFolderId,
        });
      }
      importView.current.addFiles(initialFiles);
    }
  }, [initialFiles, initialTab, onRequestClose, initialTargetFolderId]);

  useLayoutEffect(() => {
    if (activeTab === DNA_SEQUENCE_IMPORT_FLOW.FILE) {
      if (importView.current != null) {
        importView.current.remove();
      }
      importView.current = new ImportFromFileView({
        closeModal: onRequestClose,
        targetFolderId: initialTargetFolderId,
        ...(importView.current != null && {uploadFiles: importView.current.uploadFiles}),
      });
      importView.current.render();
    }
  }, [activeTab, onRequestClose, initialTargetFolderId]);

  const handleTabClick = (tab: DNA_SEQUENCE_IMPORT_FLOW): void => {
    // First unrender the Backbone view
    if (tab !== DNA_SEQUENCE_IMPORT_FLOW.FILE) {
      $('#import').empty();
    }

    setActiveTab(tab);
    setSequenceImportOptions({...defaultSequenceImportOptions, folderId: sequenceImportOptions.folderId});
    analytics.track('sequence', 'import_tab_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: tab,
    });
  };

  const getSubmitButtons = (): Array<ModalButton> => {
    switch (activeTab) {
      case DNA_SEQUENCE_IMPORT_FLOW.DB: {
        const sequencesToImport = dbImportSequences.filter(({importOptions}) => importOptions.isSelected);
        // TODO: BNCH-148641 Remove hasBlockedAddgeneSequence check once Addgene sequence URL imports are re-enabled
        const hasBlockedAddgeneSequence = sequencesToImport.some(({resultData}) =>
          isBlockedAddgeneResult(resultData)
        );
        // Unless multiple transcripts are chosen in an Ensembl import, sequences must have names specified
        const disableImport =
          sequencesToImport.length === 0 ||
          sequenceImportOptions.folderId == null ||
          hasBlockedAddgeneSequence ||
          sequencesToImport.some(
            ({importOptions}) =>
              importOptions.name.length === 0 &&
              (importOptions.accession as EnsemblDBImportAccession).transcriptIds?.length < 2
          );
        return [
          {
            text: 'Import',
            submit: true,
            onClick: () => {
              importFromDB(dbImportSequences, sequenceImportOptions, handleOnRequestClose);
            },
            disabled: disableImport,
            dataTestAttrs: {component: 'DBImportSequence', element: 'button', key: ModalButtonKey.IMPORT},
          },
        ];
      }
      case DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME:
        return [
          {
            text: 'Import',
            submit: true,
            onClick: () => {
              importSequencesByChromosomalCoords(
                chromosomalData,
                sequenceImportOptions,
                handleOnRequestClose
              );
            },
            disabled: sequenceImportOptions.folderId == null,
          },
        ];
      case DNA_SEQUENCE_IMPORT_FLOW.RAW:
        return [
          {
            text: 'Create',
            submit: true,
            onClick: () => {
              createSequence(sequenceImportOptions, rawImportData, handleOnRequestClose);
              trackNucleotideCreateModalSubmit(`${sequenceImportOptions.nucleotideType} sequence`);
            },
            disabled: sequenceImportOptions.folderId == null || rawImportData.name.length === 0,
            dataTestAttrs: {component: 'ImportSequenceView', element: 'createButton'},
          },
        ];
      default:
        return [];
    }
  };

  const handleOnRequestClose = (): void => {
    onRequestClose();
    if (!openImportResults) {
      return;
    }

    // When modal closes, open first valid uploaded file.
    const sequenceToOpen = importView.current ? importView.current.firstUploadedFile() : null;
    if (sequenceToOpen == null) {
      return;
    }

    LoadingScreen.show();
    FileStates.get()
      .openFolderItem(sequenceToOpen.get('id'))
      .always(() => {
        LoadingScreen.hide();
      });
  };

  return (
    <Modal
      enableRedesign={true}
      title="Create DNA / RNA sequence"
      showHeaderBorder={false}
      large={true}
      wrapInForm={false}
      onRequestClose={handleOnRequestClose}
      buttons={[
        {
          text: 'Close',
          cancel: true,
        },
        ...getSubmitButtons(),
      ]}
      dataTestAttrs={{component: 'ImportSequenceModal', element: 'root'}}
    >
      <TabContainer activeTab={activeTab} onRequestTabChange={handleTabClick}>
        <Tab tabKey={DNA_SEQUENCE_IMPORT_FLOW.RAW} title="Create New">
          <div className="tab-pane u-spacing-top">
            <CreateSequenceForm
              rawImportData={rawImportData}
              setRawImportData={setRawImportData}
              sequenceImportOptions={sequenceImportOptions}
              setSequenceImportOptions={setSequenceImportOptions}
            />
          </div>
        </Tab>
        <Tab tabKey={DNA_SEQUENCE_IMPORT_FLOW.FILE} title="Upload Files">
          <div id="import" className="tab-pane u-spacing-top" />
        </Tab>
        <Tab tabKey={DNA_SEQUENCE_IMPORT_FLOW.DB} title="Import from Database">
          <DBImportForm
            queryResults={dbImportSequences}
            setQueryResults={setDBImportSequences}
            sequenceImportOptions={sequenceImportOptions}
            setSequenceImportOptions={setSequenceImportOptions}
          />
        </Tab>
        {BenchlingConfig.ENABLE_CRISPR && (
          <Tab tabKey={DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME} title="Import genomic regions">
            <BatchChromosomeImportForm
              sequenceImportOptions={sequenceImportOptions}
              setSequenceImportOptions={setSequenceImportOptions}
              chromosomalCoords={chromosomalData.chromosomeCoords}
              setChromosomalCoords={(chromosomeCoords) => {
                setChromosomalData({...chromosomalData, chromosomeCoords});
              }}
              genome={chromosomalData.genome}
              setGenome={(genome) => {
                setChromosomalData({...chromosomalData, genome});
              }}
            />
          </Tab>
        )}
      </TabContainer>
    </Modal>
  );
}

export default ImportSequenceModal;
